// frontend/src/pages/model/FieldTable.jsx
// The Model page's field table (Phase 3 step 13b): one row per field with how it
// is classified. Admins edit in place; everyone else sees the same table without
// controls. Every change goes up through onChange(key, patch); the table holds
// no state of its own except the text being typed in a label.
//
// Everything shown comes from the server as text and is drawn as text (React
// escapes it); chart links are built only from integer ids (chartHref).
import React from 'react';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { typeMarker } from '../builder/builderSpec';
import ModelSelect from './ModelSelect';
import Switch from './Switch';
import {
  CHARTS_SHOWN, MAX_LABEL, ROLE_LABELS, aggregationChoices, aggregationFixedText, aggregationValue, canEditField, chartHref,
  choicePatch, filledLabel, grainChoices, grainValue, isStructured, labelPatch, roleChoices, roleValue, typeLabel,
  usageLabel,
} from './modelLogic';

const COLUMNS = [
  ['Field', 'The label the builder shows, the name in your data, and the type of value.'],
  ['Role', 'How the builder groups this field: a dimension to group by, a measure to add up, time for dates, an identifier to count, or ignore to leave it out.'],
  ['Summarize as', 'What the builder does with this field when it is used as a value: add it up, average it, count it.'],
  ['Group dates by', 'The period a date is grouped by when it goes on an axis.'],
  ['Used in', 'The saved charts that use this field.'],
  ['Show', 'Hide a field to keep it out of the builder.'],
];
export const COLUMN_COUNT = COLUMNS.length;

const muted = 'text-[var(--ui-text-muted)]';

/** A field's label: an input for admins (saved when you leave it; Enter saves, Escape cancels), text otherwise. */
function LabelCell({ field, editable, busy, failed, onChange }) {
  const name = field.display_name;
  if (!editable) return <span className="block truncate text-[13.5px] text-[var(--ui-text)]">{name}</span>;
  const commit = (el) => {
    const patch = labelPatch(field, el.value);
    if (patch) onChange(field.key, patch); else el.value = name;
  };
  return (
    <input
      // A saved label, a reload or a failed save remounts the input, so it always shows what the server has.
      key={`${field.key}\u0000${name}\u0000${failed ? 'failed' : ''}`}
      type="text" defaultValue={name} maxLength={MAX_LABEL} spellCheck={false} disabled={busy}
      aria-label={`Label of ${field.key}`} title="Rename it here. Clear the box to use the automatic name."
      onBlur={(e) => commit(e.currentTarget)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
        if (e.key === 'Escape') { e.currentTarget.value = name; e.currentTarget.blur(); }
      }}
      className="-mx-2 h-8 w-[calc(100%+1rem)] rounded-lg bg-transparent px-2 text-[13.5px] text-[var(--ui-text)] hover:bg-[var(--ui-inset)] focus:bg-[var(--ui-inset)] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
    />
  );
}

/** Which saved charts use the field, as a small disclosure. */
function UsedIn({ field }) {
  if (!(field.used_in_charts > 0)) return <span className={`text-[12.5px] ${muted}`}>Not used</span>;
  const charts = Array.isArray(field.used_by) ? field.used_by : [];
  const shown = charts.slice(0, CHARTS_SHOWN);
  const more = field.used_in_charts - shown.length;
  return (
    <details>
      <summary className="cursor-pointer select-none text-[12.5px] text-[var(--ui-text)] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30">
        {usageLabel(field)}
      </summary>
      <ul className="mt-1.5 flex flex-col gap-1 text-[12px]">
        {shown.map((c) => {
          const href = chartHref(c.id);
          const title = c.title || `Chart ${c.id}`;
          return (
            <li key={c.id} className="max-w-[220px] truncate">
              {href ? <Link to={href} className="text-[var(--ui-text-muted)] underline-offset-2 hover:text-[var(--ui-text)] hover:underline">{title}</Link> : title}
            </li>
          );
        })}
        {more > 0 && <li className={muted}>and {more} more</li>}
      </ul>
    </details>
  );
}

const FieldRow = React.memo(function FieldRow({ field, canEdit, busy, error, onChange }) {
  const editable = canEdit && canEditField(field);
  const structured = isStructured(field);
  const roles = roleChoices(field);
  const aggs = aggregationChoices(field);
  const grains = grainChoices(field);
  const name = field.display_name;
  const fixedAgg = aggregationFixedText(field);
  const shownRole = ROLE_LABELS[field.role_override || field.default_role] || '';
  const shownAgg = (aggs.find((c) => c.value === aggregationValue(field)) || {}).label;
  const shownGrain = (grains.find((c) => c.value === grainValue(field)) || {}).label;

  return (
    <>
      <tr aria-busy={busy || undefined} className={`border-t border-[var(--ui-divider)] align-top ${field.hidden ? 'opacity-60' : ''}`}>
        <td className="py-2.5 pr-3">
          <div className="flex items-start gap-2.5">
            <span className={`mt-2 w-[22px] shrink-0 font-mono text-[10px] ${muted}`} title={`${typeLabel(field)}, ${filledLabel(field)}`} aria-hidden="true">{typeMarker(field)}</span>
            <div className="min-w-0 flex-1">
              <LabelCell field={field} editable={editable} busy={busy} failed={Boolean(error)} onChange={onChange} />
              <div className={`mt-0.5 flex min-w-0 items-center gap-1.5 text-[11px] ${muted}`}>
                <span className="shrink-0">{typeLabel(field)}</span>
                <span aria-hidden="true">·</span>
                <span className="truncate font-mono" title={field.sample_value ? `${field.key} (example: ${field.sample_value})` : field.key}>{field.key}</span>
                {field.is_custom && <span className="shrink-0">· custom</span>}
                {field.hidden && <span className="shrink-0 rounded bg-[var(--ui-chip)] px-1.5 text-[var(--ui-text)]">Hidden</span>}
              </div>
            </div>
            {busy && <Loader2 className={`mt-2 h-3.5 w-3.5 shrink-0 animate-spin ${muted}`} role="status" aria-label="Saving" />}
          </div>
        </td>
        <td className="w-[185px] py-2.5 pr-3">
          {structured ? <span className={`text-[12.5px] ${muted}`} title="Lists and objects can't be charted">Not chartable</span>
            : editable ? (
              <ModelSelect label={`Role of ${name}`} value={roleValue(field)} choices={roles} disabled={busy}
                onChange={(v) => onChange(field.key, choicePatch('role', v))} />
            ) : <span className="text-[12.5px] text-[var(--ui-text)]">{shownRole}</span>}
        </td>
        <td className="w-[205px] py-2.5 pr-3">
          {aggs.length > 0 && editable ? (
            <ModelSelect label={`Summarize ${name} as`} value={aggregationValue(field)} choices={aggs} disabled={busy}
              onChange={(v) => onChange(field.key, choicePatch('default_aggregation', v))} />
          ) : (
            <span className={`text-[12.5px] ${muted}`}>{aggs.length > 0 ? shownAgg : (fixedAgg || '—')}</span>
          )}
        </td>
        <td className="w-[150px] py-2.5 pr-3">
          {grains.length > 0 && editable ? (
            <ModelSelect label={`Group ${name} by`} value={grainValue(field)} choices={grains} disabled={busy}
              onChange={(v) => onChange(field.key, choicePatch('default_grain', v))} />
          ) : (
            <span className={`text-[12.5px] ${muted}`}>{grains.length > 0 ? shownGrain : '—'}</span>
          )}
        </td>
        <td className="py-2.5 pr-3"><UsedIn field={field} /></td>
        <td className="py-2.5">
          {structured ? <span className={`text-[12.5px] ${muted}`}>—</span>
            : editable ? (
              <Switch checked={!field.hidden} disabled={busy} label={`Show ${name} in the builder`}
                title={field.hidden ? 'Hidden from the builder' : 'Shown in the builder'}
                onChange={(show) => onChange(field.key, { hidden: !show })} />
            ) : <span className="text-[12.5px] text-[var(--ui-text)]">{field.hidden ? 'Hidden' : 'Shown'}</span>}
        </td>
      </tr>
      {error && (
        <tr>
          <td colSpan={COLUMN_COUNT} className="pb-2.5">
            <p role="alert" className="rounded-lg bg-red-500/10 px-3 py-1.5 text-xs text-red-300">{error}</p>
          </td>
        </tr>
      )}
    </>
  );
});

/**
 * fields: the model's fields (already filtered). busy: { [key]: true } for fields being saved.
 * errors: { [key]: text }. onChange(key, patch) must be stable between renders.
 */
export default function FieldTable({ fields, canEdit, busy = {}, errors = {}, onChange, emptyText = 'No fields match.' }) {
  return (
    <div className="overflow-x-auto scrollbar-thin">
      <table className="w-full min-w-[900px] border-collapse text-left">
        <thead>
          <tr>
            {COLUMNS.map(([label, help]) => (
              <th key={label} scope="col" title={help || undefined} className={`pb-2 pr-3 text-xs font-normal ${muted}`}>{label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {fields.map((f) => (
            <FieldRow key={f.key} field={f} canEdit={canEdit} busy={Boolean(busy[f.key])} error={errors[f.key] || ''} onChange={onChange} />
          ))}
          {fields.length === 0 && (
            <tr><td colSpan={COLUMN_COUNT} className={`border-t border-[var(--ui-divider)] py-8 text-center text-sm ${muted}`}>{emptyText}</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
